// src/pages/ielts/reading/ReadingSoalPage.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PassageText from "../../../components/Ielts_test/soal/reading/PassageText";
import QuestionItem from "../../../components/Ielts_test/soal/reading/QuestionItem";
import ResultModal from "../../../components/Ielts_test/hasil/reading/ResultModal";
import {
  getIetlsReadingQuestions,
  submitIetlsReadingAnswer,
} from "../../../hooks/useApi";

export default function ReadingSoalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [currentPart, setCurrentPart] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [startTime] = useState(Date.now());
  const [duration, setDuration] = useState(0);
  const questionRefs = useRef([]);
  const [isRunning, setIsRunning] = useState(true);

  // Timer
  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, isRunning]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getIetlsReadingQuestions();
        setData(res);
        questionRefs.current = res.parts.flatMap((p) =>
          p.sections.flatMap((s) => s.questions.map(() => null)),
        );
      } catch {
        alert("Passage tidak ditemukan");
        navigate("/reading-test");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleAnswer = (answer) => {
    setAnswers((prev) => ({ ...prev, [answer.question_id]: answer }));
  };

  const scrollToQuestion = (index) => {
    questionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const handleSubmit = async () => {
    const total = data.parts.flatMap((p) =>
      p.sections.flatMap((s) => s.questions),
    ).length;
    const answered = Object.keys(answers).length;
    if (answered < total) {
      setShowConfirm(true);
      return;
    }
    await submitAnswers();
  };

  function normalizeIeltsResult(detailAnswers, questionOrderMap) {
    return detailAnswers
      .map((ans) => {
        const order = questionOrderMap[ans.question_id];

        return {
          ...ans,
          number: order?.number ?? null,
          passageIndex: order?.passageIndex ?? null,
          questionIndex: order?.questionIndex ?? null,
        };
      })
      .sort((a, b) => a.number - b.number);
  }

  const submitAnswers = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const payload = Object.values(answers);
      const res = await submitIetlsReadingAnswer(payload);
      setIsRunning(false);

      const orderedDetailAnswers = normalizeIeltsResult(
        res.detail_answers,
        data.questionOrderMap,
      );

      setResult({
        ...res,
        detail_answers: orderedDetailAnswers,
      });
    } catch (err) {
      alert(err.message || "Gagal mengumpulkan jawaban");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );

  if (!data) return null;

  const part = data.parts[currentPart];
  const totalAnswered = Object.keys(answers).length;
  const flatQuestions = part.sections.flatMap((s) => s.questions);

  return (
    <>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-md p-4 mb-2 text-center">
          <h1 className="font-anton text-2xl md:text-3xl text-primary">
            READING
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            You should spend about 20 minutes on Questions{" "}
            {part.sections[0].startNumber}–{part.sections.at(-1).endNumber}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Passage */}
          <div className="bg-white rounded-xl shadow-md p-5 overflow-y-auto max-h-[70vh]">
            {part.passage_id !== "NO_PASSAGE" && (
              <h2 className="font-anton text-xl text-primary mb-3">
                READING PASSAGE {currentPart + 1}
              </h2>
            )}

            {part.content && (
              <PassageText
                passage={{ title: part.passage_title, content: part.content }}
              />
            )}
          </div>

          {/* Questions */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-md p-5 max-h-[70vh] overflow-y-auto space-y-8">
              {part.sections.map((section, sectionIdx) => (
                <div
                  key={section.section_id ?? sectionIdx}
                  className="space-y-4"
                >
                  {/* Instruction Block (IELTS style) */}
                  <div className="bg-gray-100 border-l-4 border-primary p-4 rounded">
                    <p className="font-anton text-primary">
                      Questions {section.startNumber}–{section.endNumber}
                    </p>
                    <p className="font-poppins text-sm text-gray-700 mt-1">
                      {section.instruksi_title}
                    </p>
                  </div>

                  {/* Questions */}
                  {section.questions.map((q, idx) => {
                    const globalIdx =
                      data.parts
                        .slice(0, currentPart)
                        .reduce(
                          (sum, p) =>
                            sum +
                            p.sections.reduce(
                              (s, sec) => s + sec.questions.length,
                              0,
                            ),
                          0,
                        ) +
                      part.sections
                        .slice(0, sectionIdx)
                        .reduce((s, sec) => s + sec.questions.length, 0) +
                      idx;

                    return (
                      <div
                        key={q.id}
                        ref={(el) => (questionRefs.current[globalIdx] = el)}
                        className="scroll-mt-24"
                      >
                        <QuestionItem
                          question={q}
                          onAnswer={handleAnswer}
                          userAnswer={answers[q.id]}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Number Navigation */}
        <div className="mt-6 w-full flex justify-center">
          <div className="bg-gray-50 rounded-xl p-4 shadow-inner max-w-3xl w-full">
            <div className="text-center text-xs text-gray-500 mb-2 font-poppins">
              Questions {part.sections[0].startNumber}–
              {part.sections.at(-1).endNumber}
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {flatQuestions.map((q, i) => {
                const answered = answers[q.id];
                const globalIdx =
                  data.parts
                    .slice(0, currentPart)
                    .reduce(
                      (sum, p) =>
                        sum +
                        p.sections.reduce(
                          (s, sec) => s + sec.questions.length,
                          0,
                        ),
                      0,
                    ) + i;

                return (
                  <button
                    key={q.id}
                    onClick={() => scrollToQuestion(globalIdx)}
                    className={`
        w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
        ${answered ? "bg-accent text-white" : "bg-white border border-gray-300"}
      `}
                  >
                    {q.number}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          <button
            onClick={() => setCurrentPart(Math.max(0, currentPart - 1))}
            disabled={currentPart === 0}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl font-poppins disabled:opacity-50 hover:bg-gray-300 w-full sm:w-auto"
          >
            Previous Part
          </button>

          <div className="text-sm font-poppins text-gray-600">
            {totalAnswered} / {data.total_questions} answered
          </div>

          {currentPart < data.parts.length - 1 ? (
            <button
              onClick={() => setCurrentPart(currentPart + 1)}
              className="px-6 py-2 bg-primary text-white rounded-xl font-poppins font-semibold hover:bg-secondary w-full sm:w-auto"
            >
              Next Part
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-2 bg-accent text-primary rounded-xl font-poppins font-bold hover:scale-105 disabled:opacity-50 w-full sm:w-auto"
            >
              {submitting ? "Submitting..." : "Submit All"}
            </button>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center">
            <h3 className="font-anton text-2xl text-primary mb-3">
              Belum Semua Dijawab
            </h3>
            <p className="font-poppins text-gray-700 mb-6">
              Kamu baru menjawab <strong>{totalAnswered}</strong> dari{" "}
              <strong>{data.total_questions}</strong> soal.
            </p>
            <div className="flex gap-3">
              <button
                onClick={submitAnswers}
                className="flex-1 py-3 bg-primary text-white rounded-xl font-poppins font-semibold hover:bg-secondary"
              >
                Tetap Submit
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-3 bg-gray-200 text-gray-800 rounded-xl font-poppins font-semibold hover:bg-gray-300"
              >
                Lanjut Jawab
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {result && (
        <ResultModal
          result={result}
          data={data}
          duration={duration}
          onClose={() => setResult(null)}
          test_type="Reading"
        />
      )}
    </>
  );
}
